import { getEnvVar } from './env';

/**
 * Centrally detects the Firebase Functions base URL based on the current environment.
 * 
 * Logic flow:
 * 1. Check for window.__FIREBASE_CONFIG__ (set by production build hooks).
 * 2. Check hostname for production/staging domain detection fallback.
 * 3. Use environment variables (NX_API_BASE_URL or NX_FIREBASE_PROJECT_ID).
 * 4. Fallback to default dev project ID (fp-dash-dev).
 * 
 * @returns string The base URL for Firebase Functions (e.g., https://us-central1-fp-dash-dev.cloudfunctions.net)
 */
export function getApiBaseUrl(): string {
  // First priority: Production config injected via script tags (common in the production build)
  if (typeof window !== 'undefined' && (window as any).__FIREBASE_CONFIG__) {
    const projectId = (window as any).__FIREBASE_CONFIG__.projectId;
    return `https://us-central1-${projectId}.cloudfunctions.net`;
  }

  const isProductionHostname = typeof window !== 'undefined' && (
    window.location.hostname === 'canvasestate.com.au' ||
    window.location.hostname === 'www.canvasestate.com.au' ||
    window.location.hostname === 'canvasestate.com' ||
    window.location.hostname === 'www.canvasestate.com' ||
    window.location.hostname.endsWith('canvasestate.com.au') ||
    window.location.hostname.endsWith('canvasestate.com') ||
    window.location.hostname === 'financial-portfolio-dash.web.app' ||
    window.location.hostname === 'financial-portfolio-dash.firebaseapp.com'
  );

  // Second priority: Production hostname-based fallback
  if (isProductionHostname) {
    return 'https://us-central1-financial-portfolio-dash.cloudfunctions.net';
  }

  // Third priority: Environment variables (useful for CI and custom builds)
  const apiBaseUrl = getEnvVar('NX_API_BASE_URL');
  if (apiBaseUrl) return apiBaseUrl;

  // Fourth priority: Handle local emulator if needed (for localhost development)
  const useDeployedFunctions = getEnvVar('VITE_USE_DEPLOYED_FUNCTIONS') === 'true' ||
    getEnvVar('NX_USE_DEPLOYED_FUNCTIONS') === 'true';
  const emulatorHost = getEnvVar('API_EMULATOR_HOST') || getEnvVar('REACT_APP_API_EMULATOR_HOST');

  if (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost') {
    // If using deployed functions or emulator host points to cloud, skip emulator
    if (useDeployedFunctions || (emulatorHost && emulatorHost.startsWith('https://'))) {
      const devProjectId = getEnvVar('NX_FIREBASE_PROJECT_ID') || 'fp-dash-dev';
      return emulatorHost || `https://us-central1-${devProjectId}.cloudfunctions.net`;
    }
    // Otherwise use local emulator
    const devProjectId = (window as any).__FIREBASE_CONFIG__?.projectId || getEnvVar('NX_FIREBASE_PROJECT_ID') || 'fp-dash-dev';
    const localEmulatorHost = emulatorHost || 'http://localhost:5001';
    // Emulator URL format: http://localhost:5001/<projectId>/us-central1
    return `${localEmulatorHost}/${devProjectId}/us-central1`;
  }

  // Final Fallback: Default dev project ID
  const devProjectId = getEnvVar('NX_FIREBASE_PROJECT_ID') || getEnvVar('VITE_FIREBASE_PROJECT_ID') || 'fp-dash-dev';
  return `https://us-central1-${devProjectId}.cloudfunctions.net`;
}
